import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import lodash from 'lodash';
import GraphView from './GraphView';
import NodeEditor from './NodeEditor';
import ConfirmationModal from "../../../../../components-new/modals/ConfirmationModal";
import { t } from "../../../../../components/Translate";
import GraphActions from "./GraphActions";

const GraphEditor = ({
                         nodes,
                         edges,
                         rootNodeId,
                         editNode,
                         deleteNode,
                         addNotification,
                         addNode,
                         connectButtonToDestination,
                         removeButtonConnection,
                         user,
                         company,
                         tariff,
                         isFetching
                     }) => {
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [deletingNode, setDeletingNode] = useState(false);
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
    const [removingNode, setRemovingNode] = useState({});
    const [nodeEditorInitialFlags, setNodeEditorInitialFlags] = useState([]); //addButton

    const selectNode = useCallback((node,flags = []) => {
        if(node.id === selectedNodeId || isFetching) return
        setSelectedNodeId(null);
        setNodeEditorInitialFlags(flags);
        setTimeout(() => setSelectedNodeId(node.id), 100);
    }, [selectedNodeId,isFetching]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                selectNode({ id: null });
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [selectNode]);

    const cleanNodeSelection = useCallback(() => {
        if (selectedNodeId !== null) setSelectedNodeId(null);
    }, [selectedNodeId]);

    const handleEditNode = useCallback(
        (node) => {
            return editNode(node)
                .then(() => {
                    setSelectedNodeId(null);
                })
        },
        [editNode, addNotification]
    );

    const openConfirmationDialog = useCallback((node) => {
        setIsConfirmationDialogOpen(true);
        setRemovingNode(node);
    }, []);

    const closeConfirmationDialog = useCallback(() => {
        setIsConfirmationDialogOpen(false);
        setRemovingNode({});
    }, []);

    const confirmDeleteNode = useCallback(() => {
        deleteNode(removingNode)
            .then(() => setSelectedNodeId(null))
            .finally(() => setDeletingNode(false));
        closeConfirmationDialog();
    }, [deleteNode, removingNode, closeConfirmationDialog]);

    /*
     * Does anything hang off this node?
     *
     * A node that hands the chat to an operator ends its branch - it sends no
     * buttons, so whatever those buttons lead to becomes unreachable. Rather
     * than silently orphaning a subtree, the editor refuses the change and says
     * why. Asked here because this is where the edges are; the editor itself
     * only ever sees one node.
     */
    const selectedNodeHasChildren = useMemo(() => {
        const node = nodes.find((n) => n.id === selectedNodeId);
        if (!node || !Array.isArray(node.buttons)) return false;

        return node.buttons.some(
            (button) => edges.some((edge) => edge.buttonId === button.id)
        );
    }, [nodes, edges, selectedNodeId]);

    const selectedNode = useMemo(
        () => lodash.cloneDeep(nodes.find((n) => n.id === selectedNodeId)),
        [nodes, selectedNodeId]
    );

    return (
        <div
            className={`graph ${isFetching ? 'loading' : ''}`}
            style={{
                width: '100%',
                gap: '10px',
                overflow: 'hidden',
            }}
        >
            <GraphView
                nodesData={nodes}
                edgesData={edges}
                rootNodeId={rootNodeId}
                cleanSelectedNode={cleanNodeSelection}
                selectNode={selectNode}
                addNode={addNode}
                connectButtonToDestination={connectButtonToDestination}
                removeButtonConnection={removeButtonConnection}
                onDeleteNode={openConfirmationDialog}
                editNode={handleEditNode}
                isFetching={isFetching}
            />

            {selectedNode && (
                <NodeEditor
                    node={selectedNode}
                    editingNode={isFetching}
                    deletingNode={deletingNode}
                    addNotification={addNotification}
                    editNode={handleEditNode}
                    deleteNode={openConfirmationDialog}
                    user={user}
                    company={company}
                    tariff={tariff}
                    hasChildren={selectedNodeHasChildren}
                    initialFlags={nodeEditorInitialFlags}
                    onClose={()=> selectNode({ id: null })}
                />
            )}

            <GraphActions />

            {isConfirmationDialogOpen && (
                <ConfirmationModal
                    variant="delete"
                    isOpen={isConfirmationDialogOpen}
                    onClose={() => setIsConfirmationDialogOpen(false)}
                    onConfirm={confirmDeleteNode}
                    message={t({
                        text: 'areYouSureYouWantToProceed',
                        user,
                    })}
                />
            )}
        </div>
    );
};

GraphEditor.propTypes = {
    nodes: PropTypes.array.isRequired,
    edges: PropTypes.array.isRequired,
    rootNodeId: PropTypes.number.isRequired,
    editNode: PropTypes.func.isRequired,
    deleteNode: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
    addNode: PropTypes.func.isRequired,
    connectButtonToDestination: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,
};

export default GraphEditor;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/graph/containers/GraphEditor.jsx